/* eslint-disable no-nested-ternary */
/**
 * @name FiltersService
 * @description Servicio de filtros para modificar el formato de texto, fecha o elemento de React
 * @author Ricardo Guerra
 */

import React from 'react';
import i18n from 'i18next';
import moment from 'moment';
import styled from 'styled-components';

import { ProgressBar } from '@components';
import { DownloadFileService } from '@services';
import { Avatar, Badge, Flex, MUI, Unicons, Theme, Typography } from '@styles';

// Filtro para mostrar 'Activo' o 'Inactivo' desde un boolean.
const ActiveFilter = (item) => {
  return item ? (
    <Typography.Text>{i18n.t('StatusText.active')}</Typography.Text>
  ) : (
    <Typography.Text>{i18n.t('StatusText.inactive')}</Typography.Text>
  );
};

// Filtro para mostrar un badge 'Activo' o 'Inactivo' desde un boolean.
const ActiveBadgeFilter = (item) => {
  return item ? (
    <Badge showBadge={true} showDot={true} color={Theme.colors.green} backgroundColor={Theme.colors.greenAlpha}>{i18n.t('StatusText.active')}</Badge>
  ) : (
    <Badge showBadge={true} showDot={true} color={Theme.colors.greyDarkAlpha} backgroundColor={Theme.colors.greyHover}>{i18n.t('StatusText.inactive')}</Badge>
  );
};

// Filtro para mostrar icono de 'Check' o 'Remove' desde un boolean.
const ActiveIconFilter = (item) => {
  return item ? (
    <IconBox>
      <Unicons.CircleCheck size="20" color={Theme.colors.green} />
    </IconBox>
  ) : (
    <IconBox>
      <Unicons.CircleMinus size="20" color={Theme.colors.red} />
    </IconBox>
  );
};

// Filtro para mostrar 'Sí' o 'No' desde un boolean.
const YesNoFilter = (item) => {
  return item ? (
    <Typography.Text>{i18n.t('StatusText.yes')}</Typography.Text>
  ) : (
    <Typography.Text>{i18n.t('StatusText.not')}</Typography.Text>
  );
};

// Filro para mostar un Avatar.
const AvatarFilter = (url) => {
  return (
    <Avatar size="xs" src={url ? url : 'https://assets.talentry.io/icons/profile/TAL_Avatar.png'} />
  );
};

// Filtro de fecha corto - EJEMPLO: DD/MM/YYYY
const ShortDateFilter = (item) => {
  return (item === undefined || item === '0001-01-01T00:00:00') ? '' : (
    <Typography.Text>{moment(item).format(i18n.t('FormatDates.short'))}</Typography.Text>
  );
};

// Filtro de fecha largo - EJEMPLO: DD/MM/YYYY HH:MM AM/PM
const LongDateFilter = (item) => {
  return (item === undefined || item === '0001-01-01T00:00:00') ? '' : (
    <Flex.Col>
      <Typography.Text>{moment(item).format(i18n.t('FormatDates.short'))}</Typography.Text>
      <Typography.Text color="greyDarkAlpha">{moment(item).format('hh:mm A')}</Typography.Text>
    </Flex.Col>
  );
};

// Filtro fecha para cumpleaños - EJEMPLO: DD/MM
const BirthdayDateFilter = (item) => {
  return (item === undefined || item === '0001-01-01T00:00:00') ? '' : (
    <Typography.Text>{moment(item).format(i18n.t('FormatDates.birthday'))}</Typography.Text>
  );
};

// Filtro de día y fecha - EJEMPLO: Lunes DD/MM/YYYY
const DayNameShortDateFilter = (item) => {
  return (
    <Flex.Col>
      <Typography.Text color="greyDarkAlpha">{item === '0001-01-01T00:00:00' ? '' : moment(item).format('dddd.')}</Typography.Text>
      <Typography.Text>{item === '0001-01-01T00:00:00' ? '' : moment(item).format(i18n.t('FormatDates.short'))}</Typography.Text>
    </Flex.Col>
  );
};

// Filtro de día y fecha - EJEMPLO: Lunes DD MMM. YYYY
const DayNameDateFilter = (item) => {
  return (
    <Flex.Col>
      <Typography.Text color="greyDarkAlpha">{item === '0001-01-01T00:00:00' ? '' : moment(item).format('dddd.')}</Typography.Text>
      <Typography.Text>{item === '0001-01-01T00:00:00' ? '' : moment(item).format('DD MMM. YYYY')}</Typography.Text>
    </Flex.Col>
  );
};

// Filtro de horario de 12 hrs - EJEMPLO: HH:MM AM/PM
const Time12HoursFilter = (item) => {
  return moment(item, 'hh:mm:ss').format('hh:mm A');
};

const MonthNameFilter = (item) => {
  const monthsName = [
    i18n.t('Months.jan'),
    i18n.t('Months.feb'),
    i18n.t('Months.mar'),
    i18n.t('Months.apr'),
    i18n.t('Months.may'),
    i18n.t('Months.jun'),
    i18n.t('Months.jul'),
    i18n.t('Months.aug'),
    i18n.t('Months.sep'),
    i18n.t('Months.oct'),
    i18n.t('Months.nov'),
    i18n.t('Months.dec')
  ];
  return monthsName[item - 1];
};

// Servicio para realizar la descarga
const downloadFile = (data) => {
  DownloadFileService.download(data);
};

const DownloadFileFilter = (item, flag) => {
  return (
    item ? (
      <Typography.TextLink onClick={flag ? (event) => downloadFile(item, event) : null} >
        {i18n.t('UploadFile.downloadFile')}
      </Typography.TextLink>
    ) : ''
  );
};

// Filtro para los id's de buzón de denuncias.
const IdComplaintFilter = (item) => {
  return (
    item ? (
      <Typography.TextLink target="_blank" rel="noopener noreferrer">
        {item}
      </Typography.TextLink>
    ) : ''
  );
};

// Filtro para los estatus de buzón de denuncias. 
const StatusComplaintFilter = (item) => {
  if (item === 1) { // Pendiente
    return (<StatusPending>{i18n.t('StatusText.pending')}</StatusPending>);
  }
  else if (item === 2) { // En revisión
    return (<StatusInReview>{i18n.t('StatusText.inRevision')}</StatusInReview>);
  }
  else if (item === 3) { // Concluído
    return (<StatusConcluded>{i18n.t('StatusText.concluded')}</StatusConcluded>);
  }
};

// Filtro para los estatus de Check-In [1 - A tiempo, 2 - Tarde, 3 - Registro, 4 - Salida]
const StatusCheckInFilter = (item) => {
  switch (item) {
    case 1:
      return (
        <Flex.Row nowrap>
          <StatusDot status={item} />
          <Typography.Text color="green">{i18n.t('GenericMessages.onTime')}</Typography.Text>
        </Flex.Row>
      );
    case 2:
      return (
        <Flex.Row nowrap>
          <StatusDot status={item} />
          <Typography.Text color="red">{i18n.t('GenericMessages.late')}</Typography.Text>
        </Flex.Row>
      );
    case 3:
      return (
        <Flex.Row nowrap>
          <StatusDot status={item} />
          <Typography.Text color="orange">{i18n.t('GenericMessages.register')}</Typography.Text>
        </Flex.Row>
      );
    case 4:
      return (
        <Flex.Row nowrap>
          <StatusDot status={item} />
          < Typography.Text color="greyDark">{i18n.t('GenericMessages.exit')}</Typography.Text >
        </Flex.Row>
      );
  }
};

// Filtro para ver las competencias del colaborador en un Tooltip.
const CompetenciesTooltipFilter = (item) => {
  return (
    item.length > 0 ? (
      <MUI.Tooltip
        title={
          <Flex.Col padding="8px">
            {item.map((competence, i) => (
              <>
                <Flex.Row key={i}>
                  <div style={{ marginRight: '8px' }}>●</div>
                  <Typography.Small key={i}>{competence.NombreCompetencia}</Typography.Small>
                </Flex.Row>
                {(item.length !== i + 1) ? <br /> : null}
              </>
            ))}
          </Flex.Col>
        }
      >
        <Chip color="blueMediumAlpha">
          <Typography.Text><Typography.Medium>{item.length}</Typography.Medium></Typography.Text>
          <Unicons.Eye size="20" style={{ marginLeft: '8px' }} />
        </Chip>
      </MUI.Tooltip>
    ) : (
      <Chip color="greyLight">
        <Typography.Text><Typography.Medium>{'0'}</Typography.Medium></Typography.Text>
        <Unicons.EyeSlash size="20" style={{ marginLeft: '8px' }} />
      </Chip>
    )
  );
};

const ProgressBarFilter = (item) => {
  return (
    <ProgressBar showValue={true} value={item} />
  );
};

// *************************************
// STYLED COMPONENTS                               
// *************************************

const IconBox = styled.div`
  height: 20px;
  .MuiSvgIcon-root {
    font-size: 20px;
  }
`;

const StatusBox = styled.span`
  padding: 4px;
  border-radius: 6px;
`;

const StatusPending = styled(StatusBox)`
  color: ${(props) => props.theme.colors.greyDark};
  background-color: ${(props) => props.theme.colors.greyLight};
`;

const StatusInReview = styled(StatusBox)`
  color: ${(props) => props.theme.colors.blueDark};
  background-color: #0076A840; // Blue Dark (25%)
`;

const StatusConcluded = styled(StatusBox)`
  color: ${(props) => props.theme.colors.green};
  background-color: #00AF7740; // Green (25%)
`;

const Chip = styled.div`
  display: flex;
  align-items: center;
  justify-content: center;
  width: 70px;
  padding: 4px 8px;
  border-radius: 70px;
  background-color: ${(props) => props.theme.colors[props.color]};
`;

const StatusDot = styled.div`
  width: 12px;
  min-width: 12px;
  height: 12px;
  min-height: 12px;
  border-radius: 50%;
  border: ${(props) => props.status === 1 ? `2px solid ${props.theme.colors.green}` : props.status === 2 ? `2px solid ${props.theme.colors.red}` : props.status === 3 ? `2px solid ${props.theme.colors.orange}` : `2px solid ${props.theme.colors.greyDark}`};
  background-color: ${(props) => props.status === 1 ? props.theme.colors.greenAlpha : props.status === 2 ? props.theme.colors.redAlpha : props.status === 3 ? props.theme.colors.orangeAlpha : props.theme.colors.greyLight};
  margin-right: 8px;
`;

export const FiltersService = {
  ActiveFilter,
  ActiveBadgeFilter,
  ActiveIconFilter,
  AvatarFilter,
  ShortDateFilter,
  LongDateFilter,
  DayNameShortDateFilter,
  DayNameDateFilter,
  BirthdayDateFilter,
  MonthNameFilter,
  YesNoFilter,
  Time12HoursFilter,
  DownloadFileFilter,
  IdComplaintFilter,
  StatusComplaintFilter,
  StatusCheckInFilter,
  CompetenciesTooltipFilter,
  ProgressBarFilter
};